/**
 * Wrong-parker alert queue — admin triage surface for ANPR/guard reports of
 * vehicles parked in slots they're not authorised for.
 *
 * Data flow:
 *   - GET /v1/parking/wrong-parker-alerts?status=&from=&to=&cursor=
 *   - POST /v1/parking/wrong-parker-alerts/:id/resolve
 *
 * Realtime: subscribes to `parking:wrong_parker` via useParkingRealtime so a
 * fresh alert lands as a toast + soft chime + query invalidation. Chime is
 * a base64-encoded short beep so we don't ship a binary file in the bundle.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Image as ImageIcon, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { get, post, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { CursorPaginator } from '@/components/ui/cursor-paginator';
import { useCursorAccumulator } from '@/hooks/useCursorAccumulator';
import { useParkingRealtime } from '@/hooks/useParkingRealtime';
import { formatDateTime } from '@/lib/format';
import type { CursorList } from './_parking-types';

type AlertStatus = 'open' | 'resolved' | 'false_positive';
type DetectionSource = 'anpr' | 'guard' | 'resident' | 'system';

interface WrongParkerAlert {
  id: string;
  society_id?: string;
  detected_plate: string;
  slot_id?: string | null;
  expected_unit_id?: string | null;
  detection_source: DetectionSource;
  photo_ref?: string | null;
  detected_at: string;
  status: AlertStatus;
  resolution_notes?: string | null;
  resolved_at?: string | null;
  resolved_by_user_id?: string | null;
  slot?: { id: string; slot_number: string; zone?: { id: string; name: string } | null } | null;
  expected_unit?: { id: string; number: string; block?: { id: string; name: string } | null } | null;
}

const STATUS_OPTIONS: ReadonlyArray<{ value: AlertStatus | ''; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'open', label: 'Open' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'false_positive', label: 'False positive' }
];

const STATUS_BADGE: Record<AlertStatus, 'alert' | 'ok' | 'muted'> = {
  open: 'alert',
  resolved: 'ok',
  false_positive: 'muted'
};

const STATUS_LABEL: Record<AlertStatus, string> = {
  open: 'Open',
  resolved: 'Resolved',
  false_positive: 'False positive'
};

const SOURCE_LABEL: Record<DetectionSource, string> = {
  anpr: 'ANPR camera',
  guard: 'Guard',
  resident: 'Resident report',
  system: 'System'
};

const SOURCE_VARIANT: Record<DetectionSource, 'info' | 'warn' | 'ok' | 'muted'> = {
  anpr: 'info',
  guard: 'warn',
  resident: 'ok',
  system: 'muted'
};

// Short data-URI chime — generated 880 Hz / 120 ms sine to avoid bundling a
// binary. Falls back silently if Audio playback is blocked by the browser.
const CHIME_DATA_URI =
  'data:audio/wav;base64,UklGRiQEAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YQAEAAA=';

function playChime(): void {
  try {
    const audio = new Audio(CHIME_DATA_URI);
    audio.volume = 0.4;
    void audio.play().catch(() => undefined);
  } catch {
    /* ignore — autoplay may be blocked */
  }
}

export function ParkingWrongParkerPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<AlertStatus | ''>('open');
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');
  const [cursor, setCursor] = useState<string | null>(null);

  const filtersHash = useMemo(
    () => JSON.stringify({ statusFilter, from, to }),
    [statusFilter, from, to]
  );
  useEffect(() => {
    setCursor(null);
  }, [filtersHash]);

  const query = useQuery({
    queryKey: ['parking', 'wrong-parker', { statusFilter, from, to, cursor }],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (statusFilter) qs.set('status', statusFilter);
      if (from) qs.set('from', from);
      if (to) qs.set('to', to);
      if (cursor) qs.set('cursor', cursor);
      return get<CursorList<WrongParkerAlert>>(`/parking/wrong-parker-alerts?${qs.toString()}`);
    },
    placeholderData: (prev) => prev
  });

  const { items, nextCursor } = useCursorAccumulator(query.data, cursor, filtersHash);

  useParkingRealtime({
    onWrongParker: (e) => {
      playChime();
      toast.warning('New wrong-parker alert', {
        description: e.detected_plate ? `Plate ${e.detected_plate}` : 'Tap to triage'
      });
      void queryClient.invalidateQueries({ queryKey: ['parking', 'wrong-parker'] });
    }
  });

  const [resolveTarget, setResolveTarget] = useState<WrongParkerAlert | null>(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [falsePositive, setFalsePositive] = useState(false);
  const [photoTarget, setPhotoTarget] = useState<WrongParkerAlert | null>(null);

  const resolveMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: { resolution_notes: string; false_positive: boolean } }) =>
      post<WrongParkerAlert>(`/parking/wrong-parker-alerts/${id}/resolve`, body),
    onSuccess: () => {
      toast.success('Alert resolved');
      setResolveTarget(null);
      setResolutionNotes('');
      setFalsePositive(false);
      void queryClient.invalidateQueries({ queryKey: ['parking', 'wrong-parker'] });
    },
    onError: (e) => {
      toast.error(getErrorMessage(e));
    }
  });

  return (
    <PageHeader
      title="Wrong-parker alerts"
      description="Triage vehicles flagged as parked in unauthorised slots."
    >
      {/* Filters */}
      <Card className="mb-vt-4">
        <CardContent className="flex flex-wrap items-end gap-vt-3 p-vt-4">
          <div className="flex w-full items-center gap-vt-2 text-vt-13 font-medium text-vt-text-muted sm:w-auto">
            <Filter className="h-4 w-4" />
            Filters
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="wp-status">Status</Label>
            <Select
              id="wp-status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as AlertStatus | '')}
              className="w-44"
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="wp-from">From</Label>
            <Input
              id="wp-from"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="w-44"
            />
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="wp-to">To</Label>
            <Input
              id="wp-to"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="w-44"
            />
          </div>
          {(from || to || statusFilter !== 'open') && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setFrom('');
                setTo('');
                setStatusFilter('open');
              }}
            >
              Reset
            </Button>
          )}
        </CardContent>
      </Card>

      {/* List */}
      {query.isLoading ? (
        <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-44 w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <Card>
          <CardContent className="text-vt-alert">{getErrorMessage(query.error)}</CardContent>
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <CardContent>
            <EmptyState
              icon={<AlertTriangle className="h-7 w-7" />}
              title="No alerts in this window"
              description="Wrong-parker detections from cameras, guards and residents will appear here."
            />
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2 xl:grid-cols-3">
          {items.map((alert) => (
            <AlertCard
              key={alert.id}
              alert={alert}
              onResolve={() => setResolveTarget(alert)}
              onViewPhoto={() => setPhotoTarget(alert)}
            />
          ))}
        </div>
      )}

      <CursorPaginator
        hasMore={Boolean(nextCursor)}
        loading={query.isFetching}
        itemsShown={items.length}
        onLoadMore={() => nextCursor && setCursor(nextCursor)}
      />

      {/* Resolve dialog */}
      <Dialog
        open={Boolean(resolveTarget)}
        onOpenChange={(open) => {
          if (!open) {
            setResolveTarget(null);
            setResolutionNotes('');
            setFalsePositive(false);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve alert</DialogTitle>
            <DialogDescription>
              {resolveTarget?.detected_plate
                ? `Plate ${resolveTarget.detected_plate} detected in ${
                    resolveTarget.slot?.slot_number ?? 'unknown slot'
                  }.`
                : 'Mark this alert as resolved.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-vt-3 py-vt-2">
            <div className="space-y-vt-1">
              <Label htmlFor="wp-resolve-notes">Resolution notes</Label>
              <Textarea
                id="wp-resolve-notes"
                rows={4}
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                placeholder="What did you find / who was notified?"
              />
            </div>
            <label className="flex items-center gap-vt-2 text-vt-14 text-vt-text">
              <input
                type="checkbox"
                checked={falsePositive}
                onChange={(e) => setFalsePositive(e.target.checked)}
                className="h-4 w-4 accent-vt-primary"
              />
              Mark as false positive
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResolveTarget(null)} disabled={resolveMutation.isPending}>
              Cancel
            </Button>
            <Button
              loading={resolveMutation.isPending}
              onClick={() => {
                if (!resolveTarget) return;
                resolveMutation.mutate({
                  id: resolveTarget.id,
                  body: { resolution_notes: resolutionNotes, false_positive: falsePositive }
                });
              }}
            >
              <CheckCircle2 className="h-4 w-4" />
              Resolve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Photo dialog */}
      <Dialog
        open={Boolean(photoTarget)}
        onOpenChange={(open) => {
          if (!open) setPhotoTarget(null);
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detection photo</DialogTitle>
            <DialogDescription>
              {photoTarget
                ? `${photoTarget.detected_plate} · ${formatDateTime(photoTarget.detected_at)}`
                : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-center bg-vt-surface-2 p-vt-4">
            {photoTarget?.photo_ref ? (
              // photo_ref is a server-relative path; render via standard img.
              // If the asset path requires Bearer auth, swap to the blob fetch
              // pattern from useBranding.useBrandLogoBlobUrl.
              <img
                src={photoTarget.photo_ref}
                alt={`Plate ${photoTarget.detected_plate}`}
                className="max-h-[60vh] max-w-full rounded-vt-sm object-contain"
              />
            ) : (
              <div className="flex flex-col items-center gap-vt-2 py-vt-6 text-vt-text-muted">
                <ImageIcon className="h-8 w-8" />
                <p className="text-vt-13">No photo attached</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </PageHeader>
  );
}

function AlertCard({
  alert,
  onResolve,
  onViewPhoto
}: {
  alert: WrongParkerAlert;
  onResolve: () => void;
  onViewPhoto: () => void;
}): JSX.Element {
  const isOpen = alert.status === 'open';
  return (
    <Card className={isOpen ? 'border-vt-alert/40' : undefined}>
      <CardContent className="space-y-vt-3 p-vt-4">
        <div className="flex items-start justify-between gap-vt-2">
          <div>
            <div className="font-mono text-vt-22 font-semibold tracking-wide text-vt-text">
              {alert.detected_plate}
            </div>
            <div className="mt-vt-1 text-vt-12 text-vt-text-muted">
              {formatDateTime(alert.detected_at)}
            </div>
          </div>
          <Badge variant={STATUS_BADGE[alert.status]}>{STATUS_LABEL[alert.status]}</Badge>
        </div>

        <div className="grid grid-cols-2 gap-vt-2 text-vt-13">
          <div>
            <div className="text-vt-12 text-vt-text-muted">Slot</div>
            <div className="font-medium text-vt-text">
              {alert.slot?.slot_number ?? '—'}
              {alert.slot?.zone?.name ? (
                <span className="ml-1 text-vt-text-muted">· {alert.slot.zone.name}</span>
              ) : null}
            </div>
          </div>
          <div>
            <div className="text-vt-12 text-vt-text-muted">Expected unit</div>
            <div className="font-medium text-vt-text">
              {alert.expected_unit
                ? `${alert.expected_unit.block?.name ? `${alert.expected_unit.block.name}-` : ''}${
                    alert.expected_unit.number
                  }`
                : 'Unassigned'}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-vt-2">
          <Badge variant={SOURCE_VARIANT[alert.detection_source]}>
            {SOURCE_LABEL[alert.detection_source]}
          </Badge>
          {alert.photo_ref ? (
            <button
              type="button"
              onClick={onViewPhoto}
              className="inline-flex items-center gap-1 rounded-vt-sm bg-vt-surface-2 px-vt-2 py-1 text-vt-12 text-vt-text-muted transition-colors duration-vt hover:bg-vt-surface-3 hover:text-vt-text"
            >
              <ImageIcon className="h-3 w-3" />
              View photo
            </button>
          ) : null}
        </div>

        {alert.resolution_notes ? (
          <p className="rounded-vt-sm bg-vt-surface-2 p-vt-2 text-vt-12 text-vt-text-muted">
            {alert.resolution_notes}
          </p>
        ) : null}

        {isOpen ? (
          <div className="flex justify-end">
            <Button size="sm" onClick={onResolve}>
              Resolve
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
